// Request/response schemas for the account and billing API (A10): they drive Fastify validation, typed handlers
// and, later, the OpenAPI document and the web client.
import { z } from 'zod';

const digits = (n: number) => z.string().regex(new RegExp(`^\\d{${n}}$`), `${n} digits`);
export const Cpf = digits(11);
export const Cnpj = digits(14);
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
/** A public id (Plan 6 Task 6b): a UUID — internal bigint ids never leave the server (the API translates at its edge). */
export const Id = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'uuid');

/**
 * Who pays (S-p): a person (CPF, checked against the declared name and birth date) or a company (CNPJ, checked
 * active; its name comes from the Receita and is never asked for nor echoed back).
 */
export const CreateCustomerBody = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('cpf'), cpf: Cpf, name: z.string().trim().min(2).max(200), birthDate: IsoDate, isConsultant: z.boolean().default(false) }),
  z.object({ kind: z.literal('cnpj'), cnpj: Cnpj, isConsultant: z.boolean().default(false) }),
]);

/** S-z: switch the account type (a consultoria back to conta comum only while no client shares with it). */
export const AccountTypeBody = z.object({ consultoria: z.boolean() });

export const PlanBody = z.object({
  tierCode: z.string().regex(/^tier\.[a-z]+$/),
  modules: z.array(z.string().regex(/^module\.[a-z]+$/)).max(10).default([]),
  annual: z.boolean().default(false),
  /** S-r: how an activation/upgrade is paid; default: the card on file, else credit. */
  payWith: z.enum(['card', 'credit']).optional(),
});

export const SubscribeBody = PlanBody.extend({ anmEntityId: Id, trial: z.boolean().default(false) });

/** Subrogate to a customer by its document (null revokes), or to the consultant of an invite this login took (Plan 4a). */
/**
 * Stop sharing (`document: null` — sharing by a typed document answers `invite_only`), or share with the consultoria of
 * a client invite this login took; `replace` confirms swapping out another consultoria (Plan 4a.1).
 */
export const SubrogationBody = z.union([z.object({ document: z.union([Cpf, Cnpj]).nullable() }), z.object({ inviteId: Id, replace: z.boolean().default(false) })]);
export const CarteiraHideBody = z.object({ hidden: z.boolean() });
export const CancelBody = z.object({ undo: z.boolean().default(false) });

export const SubscriptionParams = z.object({ subscriptionId: Id });
export const InvoiceParams = z.object({ invoiceId: Id });
/** S-r: a top-up, by Pix or boleto (the minimum is config, checked by the route). */
export const TopupBody = z.object({ amountCents: z.number().int().positive(), billingType: z.enum(['PIX', 'BOLETO']) });
export const TopupParams = z.object({ topupId: Id });
/** S-r: card or credit. */
export const PaymentMethodBody = z.object({ method: z.enum(['card', 'credit']) });

export const ApiError = z.object({ error: z.string(), reasons: z.array(z.string()).optional() });
